import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import storageService from '../../services/storage.service';
import { PolicyRouteType } from '../../components/modals/policy/ModalPolicy';

export type ModalOptions<T = any> = {
  name: string;
  onConfirm?: (value?: T) => void;
};

type Toast = {
  open: boolean;
  autoHideDuration?: number | null;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
};

export type GeneralStateType = {
  loader: string[];
  modal: ModalOptions;
  provider: string | undefined;
  splashLoaded: boolean;
  toast: Toast;
  policyRoute: PolicyRouteType | undefined;
  toggleChat: boolean;
  showCookies: 'first' | 'second' | '';
};

const initialState: GeneralStateType = {
  loader: [],
  modal: {
    name: '',
    onConfirm: undefined,
  },
  provider: undefined,
  splashLoaded: !!storageService.getLocalItem<boolean>('splashLoaded'),
  toast: {
    open: false,
    message: '',
    severity: 'success',
  },
  policyRoute: undefined,
  toggleChat: false,
  showCookies: '',
};

const generalSlice = createSlice({
  name: 'general',
  initialState,
  reducers: {
    setSplashLoaded(state, action: PayloadAction<{ loaded: boolean }>) {
      state.splashLoaded = action.payload.loaded;
    },
    setProvider(state, action: PayloadAction<{ provider: string | undefined }>) {
      state.provider = action.payload.provider;
    },
    setPolicyRoute(state, action: PayloadAction<{ policy: GeneralStateType['policyRoute'] }>) {
      state.policyRoute = action.payload.policy;
    },
    showModal(state, action: PayloadAction<GeneralStateType['modal']>) {
      state.modal = action.payload;
    },
    hideModal(state) {
      state.modal = {
        name: '',
        onConfirm: undefined,
      };
    },
    showLoader(state, action: PayloadAction<{ action: string }>) {
      state.loader.push(action?.payload?.action);
    },
    hideLoader(state, action: PayloadAction<{ action: string }>) {
      state.loader = state.loader.filter((loadingAction: string) => loadingAction !== action?.payload?.action);
    },
    showToast(state, action: PayloadAction<GeneralStateType['toast']>) {
      state.toast = action.payload;
    },
    hideToast(state) {
      state.toast = {
        open: false,
        message: '',
        severity: state.toast.severity,
      };
    },
    handleToggleChat(state) {
      state.toggleChat = !state.toggleChat;
    },
    setShowCookies(state, action: PayloadAction<{ cookies: GeneralStateType['showCookies'] }>) {
      state.showCookies = action.payload.cookies;
    },
    hideCookies(state) {
      state.showCookies = '';
    },
  },
});

export const {
  hideLoader,
  showLoader,
  hideModal,
  showModal,
  setProvider,
  setSplashLoaded,
  showToast,
  hideToast,
  setPolicyRoute,
  handleToggleChat,
  setShowCookies,
  hideCookies,
} = generalSlice.actions;

export default generalSlice.reducer;
